import { ArrowRight, Smartphone, CreditCard, Megaphone, ShoppingBag, MapPin, TrendingUp, Zap } from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { useLang } from "@/i18n";

const quickPaths = [
  { icon: Smartphone, label: "Digitize menus", desc: "QR, Tablet & Stand menus for your outlets", view: "product" },
  { icon: CreditCard, label: "Add POS & payments", desc: "FORA POS Register with Order & Pay", view: "home#category-section-ordering-payments" },
  { icon: Megaphone, label: "Run campaigns", desc: "Digital signage, loyalty & AI insights", view: "home#category-section-engagement-data" },
];

const heroStats = [
  { value: "6,671", label: "Outlets live", icon: ShoppingBag },
  { value: "7", label: "Active markets", icon: MapPin },
  { value: "+12-22%", label: "Avg. order uplift range", icon: TrendingUp },
  { value: "< 8 wks", label: "Time to go-live", icon: Zap },
];

const products = [
  // myFORA
  { id: "myfora", name: "myFORA — Merchant Portal", category: "myFORA", tags: ["SW"], desc: "Central activation hub. One login, every FORA product — menu, pay, loyalty, reservations, signage.", image: "/myfora-hero.jpg", metric: "All-in-one", clickable: true, view: "product:myfora", featured: true },
  // Digital Menu (featured)
  { id: "digital-menu", name: "Digital Menu", category: "Digital Menu", tags: ["SW"], desc: "QR, Tablet & Stand — one menu, three ways to access it. Central CMS, multilingual, allergens, real-time updates.", image: "/qr-menu-poster.jpg", metric: "+22% orders", clickable: true, view: "product", featured: true },
  // Ordering & Payments
  { id: "pay-at-table", name: "Pay at Table", category: "Ordering & Payments", tags: ["SW"], desc: "Guests order first, pay later via QR. Split bills, tips, real-time POS sync.", image: "/pay-at-table-hero.jpg", metric: "Faster turnover", clickable: true, view: "product:pay-at-table" },
  { id: "pos-register", name: "FORA POS", category: "Ordering & Payments", tags: ["HW", "SW"], desc: "All-in-one cloud POS. Streamline operations and elevate every transaction.", image: "/fora-pos-hero.jpg", metric: "-15s/order", clickable: true, view: "product:pos-register" },
  { id: "pickup", name: "Pickup / Ordering", category: "Ordering & Payments", tags: ["SW"], desc: "Online & in-store pickup ordering with direct POS submission.", image: "/pickup-hero.jpg", metric: "+30% off-premise", clickable: true, view: "product:pickup" },
  // Engagement & Data
  { id: "videri-signage", name: "Videri Digital Signage", category: "Engagement & Data", tags: ["SW", "HW"], desc: "Centrally managed screens for promotions, menus, and brand campaigns.", image: "/videri-hero.jpg", metric: "+30% sales", clickable: true, view: "product:videri-signage" },
  { id: "loyalty", name: "Loyalty", category: "Engagement & Data", tags: ["SW", "SVC"], desc: "Consumer loyalty platform. Automated rewards, GDPR-compliant data.", image: "/loyalty-hero.jpg", metric: "3.2x visits", clickable: true, view: "product:loyalty" },
  { id: "rms", name: "RMS — Reservation", category: "Engagement & Data", tags: ["SW"], desc: "Online table booking with real-time availability and walk-in management.", image: "/rms-hero.jpg", metric: "+35% bookings", clickable: true, view: "product:rms" },
  { id: "pepe-ai", name: "Pepe — AI Sidekick", category: "Engagement & Data", tags: ["SW"], desc: "AI assistant for menu optimization, forecasting, and operational insights.", image: "/myfora-poster.jpg", metric: "AI-powered", clickable: true, view: "product:pepe-ai" },
  { id: "fora-intelligence", name: "FORA Intelligence", category: "Engagement & Data", tags: ["SW"], desc: "Real-time business intelligence across all FORA markets. Live dashboards, ROI calculator, market-level reports.", image: "/myfora-hero.jpg", metric: "Live data", clickable: true, view: "data" },
];

const ProductCard = ({ product, onNavigate }) => {
  const { t } = useLang();
  return (
  <button
    onClick={() => product.clickable && onNavigate(product.view || "home")}
    className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden text-left transition-all group hover:border-[#000000] cursor-pointer"
    data-testid={`product-card-${product.id}`}
  >
    <div className="h-44 overflow-hidden bg-[#fafafa] relative">
      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <div className="absolute top-3 left-3">
        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white bg-[#000000] px-2 py-1 rounded-sm">
          {product.metric}
        </span>
      </div>
    </div>
    <div className="p-5">
      <div className="flex gap-1.5 mb-2">
        {product.tags.map((tag) => (
          <span key={tag} className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555555] border border-[#e8e8e8] px-1.5 py-0.5 rounded-sm">{tag}</span>
        ))}
      </div>
      <p className="font-condensed text-xl font-bold uppercase tracking-tight text-[#000000] mb-2 group-hover:text-[#dd260e] transition-colors">{product.name}</p>
      <p className="text-xs text-[#555555] leading-relaxed line-clamp-2">{t(product.desc)}</p>
    </div>
  </button>
  );
};

const FeaturedCard = ({ product, onNavigate }) => {
  const { t } = useLang();
  return (
  <button
    onClick={() => product.clickable && onNavigate(product.view || "home")}
    className="w-full bg-[#000000] rounded-sm overflow-hidden mb-4 group cursor-pointer"
    data-testid={`product-card-${product.id}`}
  >
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div className="p-8 sm:p-10 flex flex-col justify-center">
        <div className="flex gap-1.5 mb-4 flex-wrap">
          {product.tags.map((tag) => (
            <span key={tag} className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/60 border border-white/20 px-2 py-0.5 rounded-sm">{tag}</span>
          ))}
        </div>
        <h3 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-3 group-hover:text-[#dd260e] transition-colors">{product.name}</h3>
        <p className="text-sm text-white/60 leading-relaxed mb-6 max-w-md">{t(product.desc)}</p>
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#dd260e]">{product.metric}</span>
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-white flex items-center gap-2 border-b-2 border-[#dd260e] pb-1">{t("View product")} <ArrowRight className="w-3.5 h-3.5" /></span>
        </div>
      </div>
      <div className="h-56 md:h-auto overflow-hidden">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
      </div>
    </div>
  </button>
  );
};

const MARQUEE_ITEMS = ["PAY AT TABLE", "FORA POS", "VIDERI SIGNAGE", "LOYALTY", "RMS", "PEPE AI", "MYFORA"];

export const CatalogHome = ({ onNavigate }) => {
  const { t } = useLang();
  const categories = [...new Set(products.map((p) => p.category))];
  return (
    <div data-testid="catalog-home-view">
      {/* Hero */}
      <div className="mb-14" data-testid="hero-section">
        <div className="fora-pattern rounded-sm p-8 sm:p-12 lg:p-16 relative overflow-hidden">
          <div className="relative z-10">
            <span className="fora-eyebrow-dot mb-6">{t("FORA Onboard · Coca-Cola HBC Business Units")}</span>
            <h1 className="font-condensed text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-6 mt-4 max-w-4xl">
              {t("Digital solutions")}<br />
              {t("for HoReCa outlets.")}
            </h1>
            <p className="text-white/60 text-sm sm:text-base max-w-xl mb-12 leading-relaxed">
              {t("Explore FORA's product portfolio — proven across")} {heroStats[1].value} {t("markets and")} {heroStats[0].value} {t("outlets. Find what's right for your market.")}
            </p>
            <div className="flex flex-wrap gap-8 sm:gap-14 pt-10 border-t border-white/10">
              {heroStats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-[#dd260e] mt-1.5 shrink-0" strokeWidth={1.8} />
                    <div>
                      <p className="font-condensed text-white text-3xl sm:text-4xl leading-none">{s.value}</p>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mt-2">{t(s.label)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="fora-strip py-3 mt-1 rounded-sm" data-testid="hero-marquee">
          <div className="fora-strip-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i}>
                {item}
                <span className="pip" />
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Paths */}
      <div className="mb-14" data-testid="quick-paths-section">
        <span className="cchbc-eyebrow mb-6">{t("Quick Paths")}</span>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {quickPaths.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.label}
                onClick={() => onNavigate(p.view)}
                className="bg-white border border-[#e8e8e8] rounded-sm p-6 text-left hover:border-[#000000] transition-colors group"
                data-testid={`quick-path-${p.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
              >
                <Icon className="w-5 h-5 text-[#dd260e] mb-4" strokeWidth={1.8} />
                <p className="font-condensed text-lg font-bold uppercase tracking-tight text-[#000000] mb-1 group-hover:text-[#dd260e] transition-colors">{t(p.label)}</p>
                <p className="text-xs text-[#555555] leading-relaxed">{t(p.desc)}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Products by category */}
      {categories.map((cat) => {
        const inCat = products.filter((p) => p.category === cat);
        const featured = inCat.find((p) => p.featured);
        const regular = inCat.filter((p) => !p.featured);
        const slug = cat.toLowerCase().replace(/\s+&\s+/g, "-").replace(/\s+/g, "-");
        return (
          <div key={cat} id={`category-section-${slug}`} className="mb-14" data-testid={`category-section-${slug}`}>
            <div className="flex items-end justify-between mb-6 pb-4 border-b border-[#e8e8e8]">
              <div>
                <span className="cchbc-eyebrow mb-3">{inCat.length} {inCat.length === 1 ? t("Product") : t("Products")}</span>
                <h2 className="font-condensed text-3xl font-black uppercase tracking-tight text-[#000000] mt-3">{t(cat)}</h2>
              </div>
            </div>

            {featured && <FeaturedCard product={featured} onNavigate={onNavigate} />}

            {regular.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {regular.map((p) => (
                  <ProductCard key={p.id} product={p} onNavigate={onNavigate} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <PageFooter />
    </div>
  );
};
